import React from "react";
import { motion } from "framer-motion";
import {
  Home,
  Utensils,
  Car,
  Baby,
  HeartHandshake,
  Stethoscope,
  Shield,
  BriefcaseBusiness,
  ArrowRight,
  Sparkles,
} from "lucide-react";

const categories = [
  { name: "Maid", icon: Home, tone: "sky" },
  { name: "Cook", icon: Utensils, tone: "orange" },
  { name: "Driver", icon: Car, tone: "indigo" },
  { name: "Nanny", icon: Baby, tone: "sky" },
  { name: "Caretaker", icon: HeartHandshake, tone: "orange" },
  { name: "Nurse", icon: Stethoscope, tone: "indigo" },
  { name: "Security", icon: Shield, tone: "sky" },
  { name: "Office Staff", icon: BriefcaseBusiness, tone: "orange" },
];

const toneStyles = {
  sky: {
    iconWrap:
      "bg-gradient-to-br from-sky-50 to-cyan-50 border-sky-100 text-sky-700",
    pill: "bg-sky-50 text-sky-700 border-sky-100",
    glow: "from-sky-100/70 via-transparent to-cyan-100/60",
  },
  orange: {
    iconWrap:
      "bg-gradient-to-br from-orange-50 to-amber-50 border-orange-100 text-orange-700",
    pill: "bg-orange-50 text-orange-700 border-orange-100",
    glow: "from-orange-100/70 via-transparent to-amber-100/60",
  },
  indigo: {
    iconWrap:
      "bg-gradient-to-br from-indigo-50 to-violet-50 border-indigo-100 text-indigo-700",
    pill: "bg-indigo-50 text-indigo-700 border-indigo-100",
    glow: "from-indigo-100/70 via-transparent to-sky-100/50",
  },
};

const JobsCategories = () => {
  const whatsappBase =
    "https://wa.me/919665855163?text=Hi%20A2Z%20Consultancy,%20I%20want%20to%20apply%20for%20";

  const formLink = "https://forms.gle/EiqVhxZbkbqaGYZm9";

  return (
    <section className="relative py-20 md:py-28 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-[linear-gradient(180deg,#fffdfa_0%,#f7fbff_45%,#f9fbff_100%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_12%_18%,rgba(56,189,248,0.14),transparent_24%),radial-gradient(circle_at_84%_20%,rgba(99,102,241,0.10),transparent_22%),radial-gradient(circle_at_82%_78%,rgba(251,146,60,0.12),transparent_26%)]" />

      {/* Blobs */}
      <div className="absolute top-[-100px] right-[-80px] w-[240px] h-[240px] bg-sky-200/40 rounded-full blur-3xl opacity-70" />
      <div className="absolute bottom-[-100px] left-[-80px] w-[240px] h-[240px] bg-orange-200/30 rounded-full blur-3xl opacity-70" />

      {/* Texture */}
      <div className="absolute inset-0 opacity-[0.05] [background-image:radial-gradient(#0f172a_0.8px,transparent_0.8px)] [background-size:18px_18px]" />

      {/* Top wave art */}
      <svg
        className="absolute top-0 left-0 w-full h-[90px] md:h-[120px] opacity-60"
        viewBox="0 0 1440 320"
        preserveAspectRatio="none"
      >
        <path
          fill="rgba(255,255,255,0.65)"
          d="M0,96L60,101.3C120,107,240,117,360,133.3C480,149,600,171,720,170.7C840,171,960,149,1080,133.3C1200,117,1320,107,1380,101.3L1440,96L1440,0L0,0Z"
        />
      </svg>

      <div className="relative max-w-7xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
          viewport={{ once: true }}
          className="text-center mb-14 md:mb-16"
        >
          <span className="inline-flex items-center rounded-full border border-sky-200 bg-white/80 backdrop-blur px-4 py-2 text-sm text-sky-700 font-medium mb-5 shadow-sm">
            Choose Your Work Type
          </span>

          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            Jobs You Can Apply For
          </h2>

          <p className="text-gray-600 max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
            Select the kind of work you want and apply easily through our form
            or WhatsApp.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-5 md:gap-6">
          {categories.map((item, i) => {
            const Icon = item.icon;
            const tone = toneStyles[item.tone];

            return (
              <motion.a
                key={item.name}
                href={formLink}
                target="_blank"
                rel="noreferrer"
                initial={{ opacity: 0, y: 35 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: i * 0.06 }}
                viewport={{ once: true }}
                whileHover={{ y: -6 }}
                className="group relative overflow-hidden rounded-[28px] border border-white/70 bg-white/80 backdrop-blur-xl p-5 md:p-6 shadow-[0_12px_35px_rgba(15,23,42,0.08)] hover:shadow-[0_18px_45px_rgba(15,23,42,0.12)] transition-all duration-300"
              >
                {/* Hover glow */}
                <div
                  className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition duration-300 bg-gradient-to-br ${tone.glow} pointer-events-none`}
                />

                {/* Dotted corner art */}
                <div className="absolute -top-3 -right-3 w-24 h-24 rounded-full bg-white/70 blur-2xl" />
                <div className="absolute top-3 right-3 opacity-[0.10]">
                  <svg width="62" height="62" viewBox="0 0 62 62" fill="none">
                    {Array.from({ length: 5 }).map((_, row) =>
                      Array.from({ length: 5 }).map((__, col) => (
                        <circle
                          key={`${row}-${col}`}
                          cx={8 + col * 12}
                          cy={8 + row * 12}
                          r="2"
                          fill="#0f172a"
                        />
                      ))
                    )}
                  </svg>
                </div>

                {/* Top row */}
                <div className="relative flex items-start justify-between gap-3 mb-5">
                  <div
                    className={`w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-2xl border ${tone.iconWrap} group-hover:scale-105 transition duration-300`}
                  >
                    <Icon className="w-7 h-7" />
                  </div>

                  <span
                    className={`inline-flex items-center justify-center w-9 h-9 rounded-full border ${tone.pill}`}
                  >
                    <Sparkles className="w-4 h-4" />
                  </span>
                </div>

                {/* Title */}
                <h3 className="relative text-xl md:text-2xl font-semibold text-gray-900 mb-4">
                  {item.name}
                </h3>

                {/* Mid visual strip */}
                <div className="relative mb-5 rounded-2xl border border-slate-100 bg-gradient-to-r from-white via-slate-50/80 to-white px-4 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm text-gray-600">
                      Work category
                    </span>
                    <span className="text-sm font-semibold text-gray-900">
                      {item.name}
                    </span>
                  </div>
                </div>

                {/* Footer CTA */}
                <div className="relative flex items-center justify-between">
                  <a
                    href={`${whatsappBase}${encodeURIComponent(
                      item.name
                    )}%20job.`}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="text-sm font-medium text-[#25D366] hover:underline z-10 relative"
                  >
                    Quick WhatsApp apply
                  </a>

                  <span className="inline-flex items-center gap-2 text-indigo-600 font-semibold group-hover:gap-3 transition-all duration-300">
                    Apply Now
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </motion.a>
            );
          })}
        </div>

        {/* Bottom decorative strip */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
          viewport={{ once: true }}
          className="mt-12 md:mt-14"
        >
          <div className="relative overflow-hidden rounded-[28px] border border-white/70 bg-white/70 backdrop-blur-xl shadow-[0_12px_35px_rgba(15,23,42,0.06)] px-5 py-5 md:px-6">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(56,189,248,0.10),transparent_22%),radial-gradient(circle_at_80%_80%,rgba(251,146,60,0.10),transparent_24%)]" />

            <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-indigo-700 mb-1">
                  Don’t see your role?
                </p>

                <p className="text-sm md:text-base text-gray-600">
                  You can still contact us and tell us what kind of work you are
                  looking for.
                </p>
              </div>

              <a
                href="https://wa.me/919665855163?text=Hi%20A2Z%20Consultancy,%20I%20am%20looking%20for%20work."
                target="_blank"
                rel="noreferrer"
                className="shrink-0"
              >
                <button className="px-5 py-3 rounded-2xl bg-indigo-600 text-white font-semibold shadow hover:scale-[1.02] transition">
                  Contact Us
                </button>
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default JobsCategories;